import React, { useState, useRef, useEffect } from "react";
import { PostTabSkeleton } from "./components/PostTabSkeleton";
import PostComponent from "../../../components/Post/Post";
import CommentSection from "../../../components/CommentSection/CommentSection";
import EmptyState from "../../../components/EmptyState";
import { useProfilePosts } from "../../../hooks/useProfilePosts";
import { useAlert } from "../../../hooks/useAlert";
import { useNavigate } from "react-router-dom";
import { api } from "../../../services/api";
import styles from "../Post.module.scss";

interface PostTabProps {
  userId: string;
  isOwnProfile: boolean;
}

const Post: React.FC<PostTabProps> = ({ userId, isOwnProfile }) => {
  const navigate = useNavigate();
  const { alert } = useAlert();
  const { items, loading, error, hasMore, loadMore, refresh, updateItem, removeItem, restoreItem } =
    useProfilePosts(userId || null);
  const [expandedPostId, setExpandedPostId] = useState<string | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // IntersectionObserver for infinite scroll
  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;

  const hasMoreRef = useRef(hasMore);
  hasMoreRef.current = hasMore;

  const loadingRef = useRef(loading);
  loadingRef.current = loading;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && hasMoreRef.current && !loadingRef.current) {
          loadMoreRef.current();
        }
      },
      { rootMargin: "200px" }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, error]);

  const handleOpenComments = (postId: string) => {
    setExpandedPostId((prev) => (prev === postId ? null : postId));
  };

  const handleLikeToggle = async (postId: string, nextLiked: boolean) => {
    updateItem(postId, (item) => ({
      ...item,
      likedByMe: nextLiked,
      post: {
        ...item.post,
        likeCount: item.post.likeCount + (nextLiked ? 1 : -1),
      },
    }));

    try {
      if (nextLiked) {
        await api.social.likePost(postId);
      } else {
        await api.social.unlikePost(postId);
      }
    } catch (err) {
      updateItem(postId, (item) => ({
        ...item,
        likedByMe: !nextLiked,
        post: {
          ...item.post,
          likeCount: item.post.likeCount + (nextLiked ? -1 : 1),
        },
      }));
      console.error("Like toggle error:", err);
    }
  };

  const handleDeletePost = async (postId: string) => {
    // Capture the item and its index before removal for potential restoration
    const itemIndex = items.findIndex((item) => item.post._id === postId);
    const deletedItem = items[itemIndex];

    // Optimistically remove the post from the UI
    removeItem(postId);
    if (expandedPostId === postId) {
      setExpandedPostId(null);
    }

    try {
      await api.social.deletePost(postId);
    } catch (err) {
      // Restore the post at its original position on API failure
      if (deletedItem && itemIndex >= 0) {
        restoreItem(deletedItem, itemIndex);
      }
      console.error("Delete post error:", err);
      alert({ message: "Failed to delete post. Please try again.", type: "error" });
    }
  };

  const handleEditPost = async (postId: string, newBody: string) => {
    // Capture the previous body for potential rollback
    const previousItem = items.find((item) => item.post._id === postId);
    const previousBody = previousItem?.post.body;

    // Optimistically update the post body in the UI
    updateItem(postId, (item) => ({
      ...item,
      post: {
        ...item.post,
        body: newBody,
      },
    }));

    try {
      const result = await api.social.editPost(postId, newBody);
      // Update with the server-confirmed values (e.g., updatedAt timestamp)
      updateItem(postId, (item) => ({
        ...item,
        post: {
          ...item.post,
          body: result.body,
          updatedAt: result.updatedAt,
        },
      }));
    } catch (err) {
      // Revert to the previous body on API failure
      if (previousBody !== undefined) {
        updateItem(postId, (item) => ({
          ...item,
          post: {
            ...item.post,
            body: previousBody,
          },
        }));
      }
      console.error("Edit post error:", err);
      alert({ message: "Failed to edit post. Please try again.", type: "error" });
    }
  };

  // Initial loading state
  if (loading && items.length === 0 && !error) {
    return (
      <div className={styles.PostPage}>
        <PostTabSkeleton />
      </div>
    );
  }

  // Error state (no items loaded yet)
  if (error && items.length === 0) {
    return (
      <div className={styles.PostPage}>
        <div className={styles.errorState}>
          <p>Could not load posts.</p>
          <button className={styles.retryButton} onClick={refresh}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Empty state
  if (!loading && !error && items.length === 0) {
    return (
      <div className={styles.PostPage}>
        <EmptyState heading="No posts yet." />
      </div>
    );
  }

  return (
    <div className={styles.PostPage}>
      {items.map((item) => {
        const isExpanded = expandedPostId === item.post._id;
        return (
          <div className={styles.postContainer} key={item.post._id}>
            <PostComponent
              item={item}
              onLikeToggle={handleLikeToggle}
              onDelete={isOwnProfile ? handleDeletePost : undefined}
              onEdit={isOwnProfile ? handleEditPost : undefined}
              onAuthorClick={(authorUserId) => navigate(`/profile?user=${authorUserId}`)}
              onOpenComments={handleOpenComments}
              commentsExpanded={isExpanded}
            />
            {isExpanded && <CommentSection postId={item.post._id} />}
          </div>
        );
      })}

      {/* Error on pagination (items already loaded) */}
      {error && items.length > 0 && (
        <div className={styles.paginationError}>
          <p>Failed to load more posts.</p>
          <button className={styles.retryButton} onClick={loadMore}>
            Retry
          </button>
        </div>
      )}

      {/* Loading indicator for pagination */}
      {loading && items.length > 0 && (
        <div className={styles.paginationLoading}>
          <div className={styles.spinner} />
        </div>
      )}

      {/* Sentinel element for IntersectionObserver */}
      {hasMore && !error && (
        <div ref={sentinelRef} className={styles.sentinel} />
      )}
    </div>
  );
};

export default Post;
